import React, { useState } from 'react';
import { useInvoices } from '../hooks/useInvoices';
import { Layout } from '../components/layout/Layout';
import RevenueChart from '../components/charts/RevenueChart';
import { useAppointmentsPG } from '../hooks/useAppointmentsPG';
import { NoDataChart } from '../components/charts/NoDataChart';
import { useBatchDownload } from '../hooks/useBatchDownload';
import DownloadProgressModal from '../components/modals/DownloadProgressModal';
import { useBusinessConfig } from '../context/BusinessConfigContext';
import { generateInvoiceReportPDF } from '../utils/invoicePdfGenerator';
import { sendInvoiceReportEmail } from '../services/emailService';

export default function InvoiceManagement({ onNavigate }) {
    const [invoicesLoaded, setInvoicesLoaded] = useState(false);
    const [startDate, setStartDate] = useState(() => {
        const today = new Date();
        const thirtyOneDaysAgo = new Date(today);
        thirtyOneDaysAgo.setDate(today.getDate() - 31);
        return thirtyOneDaysAgo.toISOString().split('T')[0];
    });
    const [endDate, setEndDate] = useState(() => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    });
    const { invoices, loading, error, fetchInvoices, clearError } = useInvoices();
    const { getMonthlyRevenueSummary } = useAppointmentsPG();
    const { downloading, progress, errors: downloadErrors, result, downloadInvoicesAsZipFile, reset: resetDownload } = useBatchDownload();
    const [showProgressModal, setShowProgressModal] = useState(false);
    const [sendingEmail, setSendingEmail] = useState(false);
    const { config: businessConfig } = useBusinessConfig();

    const handleFetchRevenue = async (startMonth, endMonth) => {
        try {
            const data = await getMonthlyRevenueSummary(startMonth, endMonth);
            return data;
        } catch (error) {
            console.error('Error fetching revenue data:', error);
            throw error;
        }
    };

    const handleFetchInvoices = async () => {
        setInvoicesLoaded(true);
        clearError();
        await fetchInvoices(startDate, endDate);
    };

    const handleInvoiceClick = (invoiceUrl) => {
        if (invoiceUrl) {
            window.open(invoiceUrl, '_blank');
        }
    };

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('he-IL', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('he-IL', {
            style: 'currency',
            currency: 'ILS'
        }).format(price);
    };

    const calculateTotal = () => {
        return invoices.reduce((sum, invoice) => sum + (invoice.price || 0), 0);
    };

    const handleDownloadAll = async () => {
        try {
            setShowProgressModal(true);
            await downloadInvoicesAsZipFile(invoices, startDate, endDate);
        } catch (error) {
            console.error('Error downloading invoices:', error);
            alert('שגיאה בהורדת החשבוניות. אנא נסה שוב.');
        }
    };

    const handleCloseProgressModal = () => {
        setShowProgressModal(false);
        resetDownload();
    };

    const handleSendEmail = async () => {
        try {
            setSendingEmail(true);

            // Generate PDF
            console.log('Generating PDF report...');
            const pdfBlob = await generateInvoiceReportPDF(
                invoices,
                startDate,
                endDate,
                businessConfig?.business_name || 'סלון יופי'
            );

            console.log('PDF generated successfully, downloading...');

            // Download PDF directly to user's computer
            const url = URL.createObjectURL(pdfBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `invoice_report_${startDate}_to_${endDate}.pdf`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            alert('הדוח הורד בהצלחה!');

            /* TODO: Email sending functionality - will be enabled later
            // Get business email from context
            const businessEmail = businessConfig?.email;

            if (!businessEmail) {
                alert('לא נמצא כתובת אימייל בהגדרות העסק. אנא הגדר כתובת אימייל בהגדרות.');
                return;
            }

            console.log('PDF generated successfully, sending email...');

            // Send email
            await sendInvoiceReportEmail(pdfBlob, {
                userEmail: businessEmail,
                businessName: businessConfig.business_name || 'סלון יופי',
                invoiceCount: invoices.length,
                dateRange: `${formatDate(startDate)} - ${formatDate(endDate)}`,
                totalAmount: formatPrice(calculateTotal())
            });

            alert('הדוח נשלח בהצלחה לאימייל שלך!');
            */
        } catch (error) {
            console.error('Error generating PDF:', error);
            alert(error.message || 'שגיאה ביצירת הדוח. אנא נסה שוב.');
        } finally {
            setSendingEmail(false);
        }
    };

    return (
        <Layout
            currentPage="invoices"
            onNavigate={onNavigate}
            headerTitle="ניהול חשבונות"
            headerSubtitle="חיפוש וצפייה בקבלות, מחזור ואי-תשלום לפי טווח תאריכים"
        >
            <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-indigo-50 p-4">
                <div className="max-w-6xl mx-auto">
                    {/* Revenue Chart Section */}
                    <RevenueChart onFetchData={handleFetchRevenue} />

                    {/* Header */}
                    <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 mb-6 mt-6">
                        <h2 className="text-xl font-bold text-gray-800 mb-6 text-right">חשבוניות וקבלות</h2>
                        <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 mb-6">
                            {/* Date Range Inputs */}
                            <div className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="text-right">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            תאריך התחלה
                                        </label>
                                        <input
                                            type="date"
                                            value={startDate}
                                            onChange={(e) => setStartDate(e.target.value)}
                                            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 text-right"
                                        />
                                    </div>

                                    <div className="text-right">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            תאריך סיום
                                        </label>
                                        <input
                                            type="date"
                                            value={endDate}
                                            onChange={(e) => setEndDate(e.target.value)}
                                            className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-all duration-200 text-right"
                                        />
                                    </div>

                                    <div className="flex items-end">
                                        <button
                                            onClick={handleFetchInvoices}
                                            disabled={loading || !startDate || !endDate}
                                            className="w-full px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-medium hover:from-purple-600 hover:to-pink-600 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl"
                                        >
                                            {loading ? 'טוען...' : 'חפש חשבוניות'}
                                        </button>
                                    </div>
                                </div>

                                {/* Error Message */}
                                {error && (
                                    <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-right">
                                        {error}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Total Sum Card - Only show when invoices are loaded */}
                        {invoices.length > 0 && (
                            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-6 mb-6">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="text-right flex-1">
                                        <h2 className="text-lg font-semibold text-gray-700 mb-2">
                                            סך הכל
                                        </h2>
                                        <div className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                                            {formatPrice(calculateTotal())}
                                        </div>
                                        <p className="text-sm text-gray-500 mt-1">
                                            {invoices.length} קבלות נמצאו
                                        </p>
                                    </div>
                                    <div className="mr-6">
                                        <div className="w-16 h-16 bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl flex items-center justify-center">
                                            <span className="text-2xl font-bold text-purple-600">₪</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Buttons Container */}
                                <div className="flex flex-col sm:flex-row gap-3">
                                    {/* Download All Button */}
                                    <button
                                        onClick={handleDownloadAll}
                                        disabled={downloading || loading || invoices.length === 0}
                                        className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-500 text-white rounded-xl font-medium hover:from-blue-600 hover:to-indigo-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                        </svg>
                                        {downloading ? 'מוריד...' : 'הורד את כל החשבוניות (ZIP)'}
                                    </button>

                                    {/* Download PDF Report Button */}
                                    <button
                                        onClick={handleSendEmail}
                                        disabled={sendingEmail || loading || invoices.length === 0}
                                        className="flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-medium hover:from-green-600 hover:to-emerald-600 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                                        </svg>
                                        {sendingEmail ? 'מייצר...' : 'הורד דוח PDF'}
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Results */}
                        {invoices.length > 0 && (
                            <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 overflow-hidden">
                                <div className="p-6 border-b border-gray-200">
                                    <h2 className="text-lg font-semibold text-gray-800 text-right">
                                        נמצאו {invoices.length} חשבוניות
                                    </h2>
                                </div>

                                {/* Desktop Table */}
                                <div className="hidden md:block overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-gray-50/80">
                                            <tr className="text-right">
                                                                                                <th className="px-6 py-4 text-sm font-medium text-gray-700">מספר חשבונית</th>
                                                <th className="px-6 py-4 text-sm font-medium text-gray-700">תאריך</th>
                                                <th className="px-6 py-4 text-sm font-medium text-gray-700">לקוח</th>
                                                <th className="px-6 py-4 text-sm font-medium text-gray-700">שירותים</th>
                                                <th className="px-6 py-4 text-sm font-medium text-gray-700">מחיר</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {invoices.map((invoice) => (
                                                <tr
                                                    key={invoice.id}
                                                    onClick={() => handleInvoiceClick(invoice.invoiceUrl)}
                                                    className="hover:bg-purple-50/50 cursor-pointer transition-colors duration-150"
                                                >
                                                    <td className="px-6 py-4 text-sm text-gray-700 text-right">
                                                        {invoice.invoiceNumber || '-'}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-gray-900 text-right">
                                                        <div className="flex items-center gap-2 justify-end">
                                                            {formatDate(invoice.invoiceCreatedAt)}
                                                            {invoice.isBatch && (
                                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800" title="תשלום מרוכז">
                                                                    💳 {invoice.appointmentCount}
                                                                </span>
                                                            )}
                                                        </div>
                                                        {invoice.hasOutsideRange && (
                                                            <div className="text-xs text-amber-600 mt-1">
                                                                ⚠️ כולל תורים מחוץ לטווח
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-gray-900 text-right">
                                                        {invoice.customerName}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-gray-700 text-right">
                                                        {invoice.serviceNames}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm font-medium text-purple-600 text-right">
                                                        {formatPrice(invoice.price)}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Mobile Cards */}
                                <div className="md:hidden divide-y divide-gray-200">
                                    {invoices.map((invoice) => (
                                        <div
                                            key={invoice.id}
                                            onClick={() => handleInvoiceClick(invoice.invoiceUrl)}
                                            className="p-4 hover:bg-purple-50/50 cursor-pointer transition-colors duration-150"
                                        >
                                                                                        {invoice.invoiceNumber && (
                                            <div className="text-xs text-gray-500 mb-2 text-right">
                                                    מספר חשבונית: {invoice.invoiceNumber}
                                                </div>
                                            )}
                                            <div className="flex justify-between items-start mb-2">
                                                <div className="text-sm font-medium text-purple-600">
                                                    {formatPrice(invoice.price)}
                                                </div>
                                                <div className="text-sm text-gray-500 flex items-center gap-1">
                                                    {formatDate(invoice.invoiceCreatedAt)}
                                                    {invoice.isBatch && (
                                                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800" title="תשלום מרוכז">
                                                            💳 {invoice.appointmentCount}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            {invoice.hasOutsideRange && (
                                                <div className="text-xs text-amber-600 mb-2 text-right">
                                                    ⚠️ כולל תורים מחוץ לטווח
                                                </div>
                                            )}
                                            <div className="text-sm font-medium text-gray-900 mb-1 text-right">
                                                {invoice.customerName}
                                            </div>
                                            <div className="text-sm text-gray-600 text-right">
                                                {invoice.serviceNames}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Empty State - After Loading */}
                        {invoicesLoaded && !loading && invoices.length === 0 && startDate && endDate && !error && (
                            <NoDataChart
                                secondaryMessage='אין חשבוניות בטווח התאריכים שנבחר' />
                        )}

                        {/* Empty State - Initial State*/}
                        {!invoicesLoaded && !loading && invoices.length === 0 && startDate && endDate && !error && (
                            <NoDataChart
                                secondaryMessage='בצע חיפוש כדי להציג חשבוניות בטווח הנבחר' />
                        )}
                    </div>
                </div>
            </div>

            {/* Download Progress Modal */}
            <DownloadProgressModal
                isOpen={showProgressModal}
                progress={progress}
                errors={downloadErrors}
                result={result}
                onClose={handleCloseProgressModal}
            />
        </Layout>
    );
}